import { useState, useMemo, useRef } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useMissedAddresses } from "../../api/gpsMissedAddresses";
import { recordDeliveriesByAddresses } from "../../api/deliveries";
import { useTelemetry } from "../../shared/useTelemetry";
import toast from "react-hot-toast";
import "./MissedAddressesModal.css";

interface MissedAddressesModalProps {
    campaignId?: string;
    flyerTypeIds?: string[];
    onClose: () => void;
}

const skipReasonLabels: Record<string, string> = {
    SkippedDuringTurn: "Sväng",
    SkippedDirectionNotEstablished: "Riktning ej klar",
    SkippedWrongRoad: "Fel väg",
    SkippedRoadNameMismatch: "Namn matchar ej",
    SkippedNullSide: "Oklar sida",
    SkippedAhead: "Ej passerad",
    SkippedWrongSide: "Fel sida",
    FilteredByConsistencyCheck: "Konsistensfilter",
};

export const MissedAddressesModal = ({
    campaignId,
    flyerTypeIds,
    onClose
}: MissedAddressesModalProps): JSX.Element => {
    const [selectedAddresses, setSelectedAddresses] = useState<Set<string>>(new Set());
    const [expandedStreets, setExpandedStreets] = useState<Set<string>>(new Set());
    const mouseDownOnOverlay = useRef(false);
    const queryClient = useQueryClient();
    const telemetry = useTelemetry();

    const today = useMemo(() => {
        const d = new Date();
        return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
    }, []);

    const { data: summary, isLoading, error } = useMissedAddresses(today, !!campaignId);

    const confirmMutation = useMutation({
        mutationFn: (addressPointIds: string[]) =>
            recordDeliveriesByAddresses({
                campaignId: campaignId!,
                addressPointIds,
                flyerTypeIds: flyerTypeIds ?? [],
            }),
        onSuccess: (response) => {
            telemetry.track("missedAddresses.confirmed", {
                createdCount: response.createdCount,
                skippedCount: response.skippedCount,
            });
            toast.success(`${response.createdCount} leveranser skapade`);
            queryClient.invalidateQueries({ queryKey: ["map", "addresses"], refetchType: "active" });
            queryClient.invalidateQueries({ queryKey: ["deliveries"] });
            queryClient.invalidateQueries({ queryKey: ["gps", "missed-addresses"] });
            setSelectedAddresses(new Set());
            onClose();
        },
        onError: (error: unknown) => {
            telemetry.trackError("missedAddresses.confirmFailed", error);
            const msg = error instanceof Error ? error.message : "Kunde inte skapa leveranser";
            toast.error(msg);
        },
    });

    const handleToggleAddress = (addressId: string) => {
        const next = new Set(selectedAddresses);
        if (next.has(addressId)) {
            next.delete(addressId);
        } else {
            next.add(addressId);
        }
        setSelectedAddresses(next);
    };

    const handleToggleStreet = (street: string) => {
        const next = new Set(expandedStreets);
        if (next.has(street)) {
            next.delete(street);
        } else {
            next.add(street);
        }
        setExpandedStreets(next);
    };

    const handleSelectAllOnStreet = (street: string) => {
        const addresses = summary?.streets.find((s) => s.street === street)?.missedAddresses ?? [];
        const next = new Set(selectedAddresses);
        addresses.forEach((a) => next.add(a.addressPointId));
        setSelectedAddresses(next);
    };

    const handleDeselectAllOnStreet = (street: string) => {
        const addresses = summary?.streets.find((s) => s.street === street)?.missedAddresses ?? [];
        const next = new Set(selectedAddresses);
        addresses.forEach((a) => next.delete(a.addressPointId));
        setSelectedAddresses(next);
    };

    const handleSelectAll = () => {
        if (!summary) return;
        const all = new Set<string>();
        summary.streets.forEach((s) =>
            s.missedAddresses.forEach((a) => all.add(a.addressPointId))
        );
        setSelectedAddresses(all);
        setExpandedStreets(new Set(summary.streets.map((s) => s.street)));
    };

    const handleDeselectAll = () => {
        setSelectedAddresses(new Set());
    };

    const handleConfirm = () => {
        if (selectedAddresses.size === 0) {
            toast.error("Välj minst en adress");
            return;
        }
        confirmMutation.mutate(Array.from(selectedAddresses));
    };

    const totalMissed = summary?.totalMissed ?? 0;

    return (
        <div
            className="missed-addr-overlay"
            onMouseDown={(ev) => { mouseDownOnOverlay.current = ev.target === ev.currentTarget; }}
            onMouseUp={(ev) => {
                if (mouseDownOnOverlay.current && ev.target === ev.currentTarget) onClose();
                mouseDownOnOverlay.current = false;
            }}
        >
            <div className="missed-addr-modal" onClick={(e) => e.stopPropagation()}>
                <div className="missed-addr-modal__header">
                    <div>
                        <h2 className="missed-addr-modal__title">Komplettera missade adresser</h2>
                        {summary && (
                            <p className="missed-addr-modal__subtitle">
                                {summary.totalIncluded} loggade, {summary.totalMissed} potentiellt missade
                            </p>
                        )}
                    </div>
                    <button
                        type="button"
                        className="missed-addr-modal__close"
                        onClick={onClose}
                        aria-label="Stäng"
                    >
                        &times;
                    </button>
                </div>

                <div className="missed-addr-modal__body">
                    {isLoading && (
                        <div className="missed-addr-modal__loading">Laddar missade adresser...</div>
                    )}

                    {error && (
                        <div className="missed-addr-modal__error">
                            Fel vid laddning: {(error as Error).message}
                        </div>
                    )}

                    {!isLoading && !error && totalMissed === 0 && (
                        <div className="missed-addr-modal__empty">
                            Inga missade adresser hittades idag.
                        </div>
                    )}

                    {!isLoading && !error && totalMissed > 0 && (
                        <>
                            <div className="missed-addr-modal__actions">
                                <button
                                    type="button"
                                    className="missed-addr-modal__action-btn"
                                    onClick={handleSelectAll}
                                >
                                    Välj alla
                                </button>
                                <button
                                    type="button"
                                    className="missed-addr-modal__action-btn"
                                    onClick={handleDeselectAll}
                                >
                                    Avmarkera alla
                                </button>
                                <div className="missed-addr-modal__selection-count">
                                    {selectedAddresses.size} av {totalMissed} valda
                                </div>
                            </div>

                            <div className="missed-addr-modal__streets">
                                {summary!.streets.map((streetGroup) => {
                                    const isExpanded = expandedStreets.has(streetGroup.street);
                                    const selectedOnStreet = streetGroup.missedAddresses.filter(
                                        (a) => selectedAddresses.has(a.addressPointId)
                                    ).length;

                                    return (
                                        <div key={streetGroup.street} className="missed-addr-modal__street">
                                            <div
                                                className="missed-addr-modal__street-header"
                                                onClick={() => handleToggleStreet(streetGroup.street)}
                                            >
                                                <span className="missed-addr-modal__street-arrow">
                                                    {isExpanded ? "\u25BC" : "\u25B6"}
                                                </span>
                                                <span className="missed-addr-modal__street-name">
                                                    {streetGroup.street}
                                                </span>
                                                <span className="missed-addr-modal__street-count">
                                                    {streetGroup.missedCount} missade
                                                    {selectedOnStreet > 0 && ` (${selectedOnStreet} valda)`}
                                                </span>
                                                <span className="missed-addr-modal__street-included">
                                                    {streetGroup.includedCount} loggade
                                                </span>
                                            </div>

                                            {isExpanded && (
                                                <div className="missed-addr-modal__addresses">
                                                    <div className="missed-addr-modal__street-actions">
                                                        <button
                                                            type="button"
                                                            className="missed-addr-modal__street-action-btn"
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                handleSelectAllOnStreet(streetGroup.street);
                                                            }}
                                                        >
                                                            Välj alla på gatan
                                                        </button>
                                                        <button
                                                            type="button"
                                                            className="missed-addr-modal__street-action-btn"
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                handleDeselectAllOnStreet(streetGroup.street);
                                                            }}
                                                        >
                                                            Avmarkera
                                                        </button>
                                                    </div>

                                                    {streetGroup.missedAddresses.map((addr) => (
                                                        <div
                                                            key={addr.addressPointId}
                                                            className={`missed-addr-modal__address ${selectedAddresses.has(addr.addressPointId) ? "selected" : ""}`}
                                                            onClick={() => handleToggleAddress(addr.addressPointId)}
                                                        >
                                                            <input
                                                                type="checkbox"
                                                                checked={selectedAddresses.has(addr.addressPointId)}
                                                                onChange={() => handleToggleAddress(addr.addressPointId)}
                                                                className="missed-addr-modal__checkbox"
                                                            />
                                                            <div className="missed-addr-modal__address-info">
                                                                <span className="missed-addr-modal__address-number">
                                                                    {addr.number ?? "?"}
                                                                </span>
                                                                <span className="missed-addr-modal__address-reason">
                                                                    {skipReasonLabels[addr.primarySkipReason] ?? addr.primarySkipReason}
                                                                </span>
                                                            </div>
                                                        </div>
                                                    ))}
                                                </div>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        </>
                    )}
                </div>

                <div className="missed-addr-modal__footer">
                    <button
                        type="button"
                        className="missed-addr-modal__footer-btn missed-addr-modal__footer-btn--cancel"
                        onClick={onClose}
                    >
                        Avbryt
                    </button>
                    <button
                        type="button"
                        className="missed-addr-modal__footer-btn missed-addr-modal__footer-btn--confirm"
                        onClick={handleConfirm}
                        disabled={selectedAddresses.size === 0 || confirmMutation.isPending}
                    >
                        {confirmMutation.isPending
                            ? "Skapar leveranser..."
                            : `Lägg till valda (${selectedAddresses.size})`}
                    </button>
                </div>
            </div>
        </div>
    );
};
